import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';

import { isPlainObject } from './common.utils';

import type { EntityType } from '../types/entities.types';

export const filterResolvedEntities = (entities: EntityType[]) =>
  entities.filter((entity: EntityType) => {
    if (entity._deleted === true) {
      return true;
    }

    return (
      (!entity.resolved || entity.resolved === false) &&
      (!entity.retryable || entity.retryable === true)
    );
  });

type EntitiesUpdatedAction = 'updated' | 'not-updated';

export const calculateLastOffsetAction = (args: {
  prevLastOffset?: number;
  nextLastOffset?: number | null;
}): EntitiesUpdatedAction => {
  const { prevLastOffset, nextLastOffset } = args;

  if (
    prevLastOffset !== undefined &&
    nextLastOffset !== undefined &&
    nextLastOffset !== null &&
    prevLastOffset < nextLastOffset
  ) {
    return 'updated';
  }

  return 'not-updated';
};

type EntityDifferences = [unknown | undefined, unknown | undefined];
type ArrayEntityDifferences = [unknown[] | undefined, unknown[] | undefined];

const withArrayItemIdentity = (sourceItem: unknown, diffItem: unknown) => {
  if (!isPlainObject(sourceItem) || !isPlainObject(diffItem)) {
    return diffItem;
  }

  const hasSourceId = Object.prototype.hasOwnProperty.call(sourceItem, '_id');
  const hasDiffId = Object.prototype.hasOwnProperty.call(diffItem, '_id');

  if (!hasSourceId || hasDiffId) {
    return diffItem;
  }

  return {
    _id: sourceItem._id,
    ...diffItem,
  };
};

const getArrayObjectIdMap = (items: unknown[]) => {
  const idMap = new Map<string, unknown>();

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];

    if (!isPlainObject(item) || !Object.prototype.hasOwnProperty.call(item, '_id')) {
      return null;
    }

    const itemId = item._id;

    if (typeof itemId !== 'string' && typeof itemId !== 'number') {
      return null;
    }

    const itemIdAsString = String(itemId);

    if (idMap.has(itemIdAsString)) {
      return null;
    }

    idMap.set(itemIdAsString, item);
  }

  return idMap;
};

const getOrderedArrayObjectIds = (left: unknown[], right: unknown[]) => {
  const seenIds = new Set<string>();

  return [...left, ...right].reduce<string[]>((orderedIds, item) => {
    if (!isPlainObject(item) || !Object.prototype.hasOwnProperty.call(item, '_id')) {
      return orderedIds;
    }

    const itemIdAsString = String(item._id);

    if (seenIds.has(itemIdAsString)) {
      return orderedIds;
    }

    seenIds.add(itemIdAsString);

    return [...orderedIds, itemIdAsString];
  }, []);
};

const toArrayEntityDifferences = (
  leftDiff: unknown[],
  rightDiff: unknown[]
): ArrayEntityDifferences => [
  leftDiff.length > 0 ? leftDiff : undefined,
  rightDiff.length > 0 ? rightDiff : undefined,
];

const appendDiffItem = (items: unknown[], item: unknown | undefined) =>
  item === undefined ? items : [...items, item];

type BuildArrayEntityDifferencesArgs = {
  left: unknown[];
  right: unknown[];
  buildDifference: (left: unknown, right: unknown) => EntityDifferences;
};

const buildArrayEntityDifferencesWith = (
  args: BuildArrayEntityDifferencesArgs
): ArrayEntityDifferences => {
  const { left, right, buildDifference } = args;
  const leftIdMap = getArrayObjectIdMap(left);
  const rightIdMap = getArrayObjectIdMap(right);

  if (leftIdMap && rightIdMap) {
    const orderedIds = getOrderedArrayObjectIds(left, right);
    const diffsById = orderedIds.reduce(
      (accumulator, itemIdAsString) => {
        const leftItem = leftIdMap.get(itemIdAsString);
        const rightItem = rightIdMap.get(itemIdAsString);

        if (leftItem === undefined && rightItem !== undefined) {
          return {
            leftDiff: accumulator.leftDiff,
            rightDiff: [...accumulator.rightDiff, rightItem],
          };
        }

        if (leftItem !== undefined && rightItem === undefined) {
          return {
            leftDiff: [...accumulator.leftDiff, leftItem],
            rightDiff: accumulator.rightDiff,
          };
        }

        if (leftItem !== undefined && rightItem !== undefined) {
          const [leftChildDiff, rightChildDiff] = buildDifference(leftItem, rightItem);

          return {
            leftDiff: appendDiffItem(
              accumulator.leftDiff,
              leftChildDiff === undefined ? undefined : withArrayItemIdentity(leftItem, leftChildDiff)
            ),
            rightDiff: appendDiffItem(
              accumulator.rightDiff,
              rightChildDiff === undefined
                ? undefined
                : withArrayItemIdentity(rightItem, rightChildDiff)
            ),
          };
        }

        return accumulator;
      },
      {
        leftDiff: [] as unknown[],
        rightDiff: [] as unknown[],
      }
    );

    return toArrayEntityDifferences(diffsById.leftDiff, diffsById.rightDiff);
  }

  const maxLength = Math.max(left.length, right.length);
  const indexRange = Array.from({ length: maxLength }, (_, index) => index);
  const diffsByIndex = indexRange.reduce(
    (accumulator, index) => {
      const leftHasIndex = index < left.length;
      const rightHasIndex = index < right.length;

      if (!leftHasIndex) {
        return {
          leftDiff: accumulator.leftDiff,
          rightDiff: [...accumulator.rightDiff, right[index]],
        };
      }

      if (!rightHasIndex) {
        return {
          leftDiff: [...accumulator.leftDiff, left[index]],
          rightDiff: accumulator.rightDiff,
        };
      }

      const [leftChildDiff, rightChildDiff] = buildDifference(left[index], right[index]);

      return {
        leftDiff: appendDiffItem(
          accumulator.leftDiff,
          leftChildDiff === undefined ? undefined : withArrayItemIdentity(left[index], leftChildDiff)
        ),
        rightDiff: appendDiffItem(
          accumulator.rightDiff,
          rightChildDiff === undefined
            ? undefined
            : withArrayItemIdentity(right[index], rightChildDiff)
        ),
      };
    },
    {
      leftDiff: [] as unknown[],
      rightDiff: [] as unknown[],
    }
  );

  return toArrayEntityDifferences(diffsByIndex.leftDiff, diffsByIndex.rightDiff);
};

export const buildArrayEntityDifferences = (
  left: unknown[],
  right: unknown[]
): ArrayEntityDifferences =>
  buildArrayEntityDifferencesWith({
    left,
    right,
    buildDifference: buildEntityDifferences,
  });

export const buildEntityDifferences = (left: unknown, right: unknown): EntityDifferences => {
  if (isEqual(left, right)) {
    return [undefined, undefined];
  }

  if (isArray(left) && isArray(right)) {
    return buildArrayEntityDifferencesWith({
      left,
      right,
      buildDifference: buildEntityDifferences,
    });
  }

  if (isPlainObject(left) && isPlainObject(right)) {
    const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
    const objectDifferences = Array.from(keys).reduce(
      (accumulator, key) => {
        const leftHasKey = Object.prototype.hasOwnProperty.call(left, key);
        const rightHasKey = Object.prototype.hasOwnProperty.call(right, key);

        if (!leftHasKey) {
          return {
            leftDiff: accumulator.leftDiff,
            rightDiff: {
              ...accumulator.rightDiff,
              [key]: right[key],
            },
          };
        }

        if (!rightHasKey) {
          return {
            leftDiff: {
              ...accumulator.leftDiff,
              [key]: left[key],
            },
            rightDiff: accumulator.rightDiff,
          };
        }

        const [leftChildDiff, rightChildDiff] = buildEntityDifferences(left[key], right[key]);

        return {
          leftDiff:
            leftChildDiff === undefined
              ? accumulator.leftDiff
              : {
                  ...accumulator.leftDiff,
                  [key]: leftChildDiff,
                },
          rightDiff:
            rightChildDiff === undefined
              ? accumulator.rightDiff
              : {
                  ...accumulator.rightDiff,
                  [key]: rightChildDiff,
                },
        };
      },
      {
        leftDiff: {} as Record<string, unknown>,
        rightDiff: {} as Record<string, unknown>,
      }
    );

    return [
      Object.keys(objectDifferences.leftDiff).length > 0 ? objectDifferences.leftDiff : undefined,
      Object.keys(objectDifferences.rightDiff).length > 0
        ? objectDifferences.rightDiff
        : undefined,
    ];
  }

  return [left, right];
};
